import React from 'react';
import { useLocation } from 'react-router-dom';
import { TourBuilderProvider } from './TourBuilderContext';

interface ConditionalTourProviderProps {
  children: React.ReactNode;
}

// Routes that should bypass tour context entirely
// Use prefix matching for showcase routes, exact matching for others
const PUBLIC_ROUTE_PREFIXES = [
  '/showcase/',
  '/test-showcase/',
];

const PUBLIC_EXACT_ROUTES = [
  '/',
  '/login',
  '/terms',
  '/privacy',
  '/cookies',
  '/disclaimer',
  '/pricing',
  '/features',
  '/data-protection',
  '/modern-slavery',
  '/environmental',
  '/bribery-policy',
  '/info',
];

const isPublicRoute = (pathname: string): boolean => {
  // Check prefix matching for dynamic routes
  const hasPublicPrefix = PUBLIC_ROUTE_PREFIXES.some(prefix => 
    pathname.startsWith(prefix)
  );
  
  // Check exact matching for static routes
  const isExactMatch = PUBLIC_EXACT_ROUTES.includes(pathname);
  
  return hasPublicPrefix || isExactMatch;
};

export const ConditionalTourProvider: React.FC<ConditionalTourProviderProps> = ({ children }) => {
  const location = useLocation();
  const isPublic = isPublicRoute(location.pathname);

  // Enhanced debugging for showcase routes
  console.log('========================================');
  console.log('🎯 [ConditionalTour] ROUTE CHECK');
  console.log('🎯 [ConditionalTour] Current pathname:', location.pathname);
  console.log('🎯 [ConditionalTour] Is public route:', isPublic);
  console.log('🎯 [ConditionalTour] Will initialize TourProvider:', !isPublic);
  console.log('🎯 [ConditionalTour] Timestamp:', new Date().toISOString());
  
  // Check if this matches any prefix
  const matchedPrefix = PUBLIC_ROUTE_PREFIXES.find(prefix => location.pathname.startsWith(prefix));
  const matchedExact = PUBLIC_EXACT_ROUTES.includes(location.pathname);
  console.log('🎯 [ConditionalTour] Matched prefix:', matchedPrefix || 'none');
  console.log('🎯 [ConditionalTour] Matched exact route:', matchedExact || 'none');
  console.log('========================================');

  // For public routes, render children directly without tour context
  if (isPublic) {
    console.log('✅ [ConditionalTour] Rendering WITHOUT TourProvider (public route)');
    return <>{children}</>;
  }

  // For authenticated routes, use full TourBuilderProvider
  console.log('🎯 [ConditionalTour] Rendering WITH TourProvider (authenticated route)');
  return <TourBuilderProvider>{children}</TourBuilderProvider>;
};
